import { Component, forwardRef, Input, viewChild, OnInit, output } from "@angular/core";
import { NgModel, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { Observable, takeUntil } from "rxjs";
import { FinancialVendor } from "../../../types/financialvendor";
import { PCPTradePartnerSelectorBaseComponent } from '../pcp-tradepartner-selector.basecomponent';

import { ErrormessageComponent } from "../../validation/errormessage/errormessage.component";
import { AutoCompleteModule } from "primeng/autocomplete";
import { CheckboxModule } from "primeng/checkbox";
import { FluidModule } from "primeng/fluid";

@Component({
    selector: "pcp-tradepartner-autocomplete",
    templateUrl: "./pcp-tradepartner-autocomplete.component.html",
    styleUrls: ["./pcp-tradepartner-autocomplete.component.scss"],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => PCPTradePartnerAutoCompleteComponent),
        }
    ],
    standalone: true,
    imports: [AutoCompleteModule, FormsModule, FluidModule, ErrormessageComponent, CheckboxModule]
})
export class PCPTradePartnerAutoCompleteComponent extends PCPTradePartnerSelectorBaseComponent implements OnInit {

    readonly tradePartnerSelection = viewChild("tradePartnerSelection");

    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    @Input() set tradePartners(tradePartners: FinancialVendor[]) {
        if (!tradePartners || tradePartners.length === 0) { return; }

        this._tradePartners = tradePartners;
        this.tradePartnerSuggestions = this.tradePartnersToAutoCompleteSuggestions(tradePartners);
    }

    get tradePartners(): FinancialVendor[] {
        return this._tradePartners;
    }

    autocompleteLabelValue = "name";
    _tradePartners: FinancialVendor[] = [];
    allTradePartners: FinancialVendor[] = [];
    recentFilter: string = "";

    @Input() placeholder: string = "Trade Partners";
    @Input() forceSelection: boolean = true;
    @Input() appendTo: string = undefined;
    @Input() allowNameChange: boolean = true;
    @Input() showFinancialName: boolean = true;

    _selectedTradePartner: FinancialVendor;

    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    @Input() set selectedTradePartner(vendor: FinancialVendor) {
        this._selectedTradePartner = vendor;
        this.propagateChange(this._selectedTradePartner);
    }

    get selectedTradePartner(): FinancialVendor {
        return this._selectedTradePartner;
    }

    _observable: Observable<FinancialVendor[]>;

    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    @Input() set tradePartnersObservable(observable: Observable<FinancialVendor[]>) {
        this._observable = observable;
        this.reloadOptions();
    };

    get tradePartnersObservable(): Observable<FinancialVendor[]> {
        return this._observable;
    }

    readonly selectedTradePartnerChange = output<FinancialVendor>();

    readonly onInputClear = output<any>();

    @Input() onCompleteFilter: (filterString: string, displayProperty: string, tradePartnersToFilter: FinancialVendor[]) => FinancialVendor[] = this.defaultFilter;
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    tradePartnerSuggestions: FinancialVendor[] = [];

    readonly focused = output<any>();

    get label(): string {
        if (this.isLoading) return this.loadingText;

        return this.placeholder;
    }

    constructor() {
        super();
    }

    ngOnInit(): void {        
        this.autocompleteLabelValue = this.showFinancialName ? "displayText" : "businessAsName";
    }

    writeValue(obj: any) {
        this.selectedTradePartner = obj;
    }

    onChange() {
        this.selectedTradePartnerChange.emit(this.selectedTradePartner);
    }

    onComplete(event: any) {
        const filterString: string = event && event.query || "";
        this.filter(filterString, this._tradePartners);
    }

    filter(filterString: string, tradePartners: FinancialVendor[]) {
        const theFilterWasCleared: boolean = filterString === "";
        this.recentFilter = filterString;

        if (theFilterWasCleared) {
            this.tradePartnerSuggestions = this.tradePartnersToAutoCompleteSuggestions(tradePartners);
            return;
        }

        const filteredTradePartners = this.onCompleteFilter(filterString, this.autocompleteLabelValue, tradePartners);

        this.tradePartnerSuggestions = filteredTradePartners;
    }

    clearTradePartner() {
        this.selectedTradePartner = null;
        // TODO: The 'emit' function requires a mandatory any argument
    this.onInputClear.emit(null);
    }

    tradePartnersToAutoCompleteSuggestions(tradePartners: FinancialVendor[]) {
        const byDisplayText = (a, b) => a.displayText > b.displayText ? 1 : -1;
        const byBusinessAsName = (a, b) => (a.businessAsName ?? a.displayText) > (b.businessAsName ?? b.displayText) ? 1 : -1;
        const sortFunction = this.showFinancialName ? byDisplayText : byBusinessAsName;

        return tradePartners.map((tradePartner: FinancialVendor) => {
            tradePartner.displayText = `${tradePartner.name.trim()}`;
            return tradePartner;
        }).sort(sortFunction);
    }

    reloadOptions(): void {
        if (!this.tradePartnersObservable) { return; }

        this.isLoading = true;
        this.tradePartnersObservable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((vendors: FinancialVendor[]) => {
                this.tradePartners = vendors;
                this.allTradePartners = vendors;
                this.isLoading = false;
                setTimeout(() => {
                    // TODO: The 'emit' function requires a mandatory any argument
                    this.tradePartnersLoaded.emit(null);
                })

            });
    }

    selectTradePartner(vendor: FinancialVendor, addIfNotPresent: boolean = false) {
        if (!vendor) { return; }

        const communityIsNotPresent = !this.tradePartnerSuggestions.some((tradePartner) => vendor.id === tradePartner.id);
        if (communityIsNotPresent && addIfNotPresent) {
            this.tradePartners.unshift(vendor);
            this.tradePartnerSuggestions = this.tradePartnersToAutoCompleteSuggestions(this.tradePartners);
        }

        this.selectedTradePartner = this.tradePartnerSuggestions.find((tradePartner) => vendor.id === tradePartner.id);
        this.selectedTradePartnerChange.emit(this.selectedTradePartner);
    }

    filterOptionsByVendorIds(vendorIds: number[]) {
        this.tradePartners = this.allTradePartners.filter((tradePartner) => vendorIds.includes(tradePartner.id));
    }

    showAllOptions() {
        this.tradePartners = this.allTradePartners;
    }

    setDisplayNameProperty(filter:boolean = false) {
        const showBusinessName = !this.showFinancialName 
                                && this.selectedTradePartner 
                                && this.selectedTradePartner.businessAsName !== null 
                                && this.selectedTradePartner.businessAsName !== undefined;

        this.autocompleteLabelValue = showBusinessName ? "businessAsName" : "displayText";

        this.filter(this.recentFilter, this._tradePartners);
    }

    private defaultFilter(filterString: string, displayProperty: string, tradePartners: FinancialVendor[]) : FinancialVendor[] {
        return tradePartners.filter((tradePartnerOption: FinancialVendor) => {
            if (this.showFinancialName) {
                return tradePartnerOption.displayText.toLowerCase().indexOf(filterString.toLowerCase()) !== -1;
            }
            else {
                return (tradePartnerOption.businessAsName ?? tradePartnerOption.displayText).toLowerCase().indexOf(filterString.toLowerCase()) !== -1;
            }
        });
    }
}
